<template>
  <div class="main-container">
    <HeroLoading v-if="loading" message="Fetching data..." />
    <HeroError
      v-if="showError"
      :title="errorTitle"
      :message="errorMessage"
      @close="closeError"
    />
    <div class="search-bar">
      <HeroSearchbar @search="handleSearch" />
    </div>

    <div class="grid-table">
      <p v-if="searchValue" class="search-label">
        Searching for: <strong class="uppercase-text">{{ searchValue }}</strong>
      </p>
      <table class="member-table">
        <thead>
          <tr>
            <th>Member Name</th>
            <th>Premium</th>
            <th>Effective Date</th>
            <th>Birthdate</th>
            <th>Payment Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="member in filteredMembers" :key="member.id">
            <td>
              <div class="member-info">
                <span class="member-icon">
                  <img src="@/assets/images/member.png" alt="" />
                </span>
                <div>
                  <div class="member-name">{{ member.name }}</div>
                  <div class="member-company">{{ member.company }}</div>
                </div>
              </div>
            </td>
            <td data-label="Premium">
              {{ Number(member.premium).toFixed(2) }}
            </td>
            <td data-label="Effective Date">
              {{ formatDateShort(member.effectiveDate) }}
            </td>
            <td data-label="Birthdate">
              {{ formatDateShort(member.birthdate) }}
            </td>
            <td data-label="Payment Status">
              <span :class="['status-pill', getStatusClass(member.status)]">
                {{ member.status }}
              </span>
            </td>
            <td>
              <button @click="viewDetails(member.id)" class="view-btn">
                View Details
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <transition name="fade">
      <HeroMemberDetails
        v-if="showModal"
        :member="selectedMember"
        :show="showModal"
        @close="closeModal"
      />
    </transition>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import HeroSearchbar from "@/components/common/HeroSearchbar.vue";
import HeroMemberDetails from "@/components/common/HeroMemberDetails.vue";
import HeroLoading from "@/components/common/HeroLoading.vue";
import HeroError from "@/components/common/HeroError.vue";
import axios from "axios";
import moment from "moment-timezone";
export default {
  name: "IMSPage",
  components: {
    HeroSearchbar,
    HeroMemberDetails,
    HeroLoading,
    HeroError,
  },
  setup() {
    const searchValue = ref("");
    const showModal = ref(false);
    const selectedMember = ref(null);
    const members = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const showError = ref(false);
    const errorMessage = ref("");
    const errorTitle = ref("Server Error");

    const filteredMembers = computed(() => {
      if (!searchValue.value) return members.value;

      const normalize = (str) =>
        str
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/ñ/gi, "n")
          .toLowerCase();

      const query = normalize(searchValue.value);

      return members.value.filter(
        (member) =>
          normalize(member.name).includes(query) ||
          normalize(member.company).includes(query)
      );
    });

    const fetchMembers = async (query) => {
      if (!query) return;

      try {
        loading.value = true;
        error.value = null;

        const res = await axios.get("/planholders", {
          params: { insured: query },
        });

        const planholders = res.data;

        const enrichedMembers = await Promise.all(
          planholders.map(async (item) => {
            const { planholderId, officeCode, planTypeId } = item;

            let companyName = "N/A";
            let mainCompany = "N/A";
            let premium = "N/A";
            let gracePeriod = 0;
            let paymentPeriod = "N/A";
            let status = "No Payments";
            let paymentMethod = "N/A";

            // Company
            try {
              const companyRes = await axios.get("/companies", {
                params: { officeCode },
              });
              const companyData = companyRes.data[0];
              companyName = companyData?.company?.name || "N/A";
              mainCompany = companyData?.main?.name || "N/A";
              gracePeriod = parseInt(companyData?.gracePeriod) || 0;
            } catch (err) {
              console.warn(`OfficeCode not found: ${officeCode}`);
            }

            // Plans
            try {
              const planRes = await axios.get("/plans", {
                params: { planTypeId },
              });
              premium = planRes.data[0]?.premium ?? "N/A";
              paymentMethod =
                {
                  M: "Monthly",
                  A: "Annual",
                  S: "Semi-Annual",
                  Q: "Quarterly",
                }[planRes.data[0]?.paymentMethod] ?? "N/A";
            } catch (err) {
              console.warn(`PlanTypeId not found: ${planTypeId}`);
            }

            // Payments
            try {
              const paymentRes = await axios.get("/payments", {
                params: { planholderId },
              });

              if (paymentRes.data.length > 0) {
                const latestPayment = paymentRes.data.reduce(
                  (latest, current) => {
                    return new Date(current.paymentPeriod) >
                      new Date(latest.paymentPeriod)
                      ? current
                      : latest;
                  }
                );

                paymentPeriod = latestPayment.paymentPeriod || "N/A";

                //console.log(paymentMethod);

                const cutoffDate = new Date(paymentPeriod);

                if (paymentMethod === "Annual") {
                  cutoffDate.setFullYear(cutoffDate.getFullYear() + 1);
                } else if (paymentMethod === "Semi-Annual") {
                  cutoffDate.setMonth(cutoffDate.getMonth() + 6);
                } else if (paymentMethod === "Quarterly") {
                  cutoffDate.setMonth(cutoffDate.getMonth() + 3);
                }

                cutoffDate.setDate(cutoffDate.getDate() + gracePeriod + 30);

                const today = new Date();
                const formattedToday = today.toISOString().split("T")[0];

                if (paymentPeriod === "N/A") {
                  status = "No Payments";
                } else {
                  const cutoffDate = new Date(paymentPeriod);

                  if (paymentMethod === "Annual") {
                  cutoffDate.setFullYear(cutoffDate.getFullYear() + 1);
                } else if (paymentMethod === "Semi-Annual") {
                  cutoffDate.setMonth(cutoffDate.getMonth() + 6);
                } else if (paymentMethod === "Quarterly") {
                  cutoffDate.setMonth(cutoffDate.getMonth() + 3);
                }

                  cutoffDate.setDate(cutoffDate.getDate() + gracePeriod + 30);

                  const formattedCutoff = cutoffDate
                    .toISOString()
                    .split("T")[0];

                  // console.log(paymentPeriod, formattedCutoff, formattedToday, paymentMethod);

                  status =
                    formattedToday <= formattedCutoff ? "Active" : "Lapsed";
                }
              }
            } catch (err) {
              console.warn(
                `No payments found for planholderId: ${planholderId}`
              );
            }

            return {
              id: planholderId,
              name: item.insured?.fullName || "N/A",
              payor: item.payor?.fullName || "N/A",
              company: companyName,
              mainCompany: mainCompany,
              premium,
              effectiveDate: formatDateShort(item.dateOfEffectivity) || "N/A",
              birthdate: formatDateShort(item.insured?.dateOfBirth) || "N/A",
              cardNum: item.imsCardNumber || "N/A",
              paymentPeriod,
              status,
              paymentMethod,
            };
          })
        );

        members.value = enrichedMembers.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      } catch (err) {
        console.error("API Error:", err);
        showError.value = true;
        errorTitle.value = "Connection Error";
        errorMessage.value =
          err.response?.status === 500
            ? "Internal Server Error. Please try again later."
            : "Unable to connect to server.";
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = (query) => {
      searchValue.value = query;
      fetchMembers(query);
    };

    const viewDetails = (id) => {
      selectedMember.value = members.value.find((m) => m.id === id);
      showModal.value = true;
    };

    const closeModal = () => {
      showModal.value = false;
      selectedMember.value = null;
    };

    const closeError = () => {
      showError.value = false;
    };

    const getStatusClass = (status) => {
      if (status === "Active") {
        return "active";
      } else if (status === "Lapsed") {
        return "lapsed";
      } else if (status === "No Payments") {
        return "no-payments";
      } else {
        return "";
      }
    };

    const formatDateShort = (isoDateString) => {
      if (!isoDateString) return "N/A";
      return moment.utc(isoDateString).tz("Asia/Manila").format("MM/DD/YYYY");
    };

    const formatDateTimeLong = (isoDateString) => {
      if (!isoDateString) return "N/A";
      return moment
        .utc(isoDateString)
        .tz("Asia/Manila")
        .format("MMMM D, YYYY h:mm A");
    };

    return {
      searchValue,
      members,
      filteredMembers,
      handleSearch,
      viewDetails,
      closeModal,
      getStatusClass,
      selectedMember,
      showModal,
      loading,
      error,
      showError,
      errorMessage,
      errorTitle,
      closeError,
      formatDateShort,
      formatDateTimeLong,
    };
  },
};
</script>

<style scoped>
.main-container {
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 20px;
  font-family: "Arial", sans-serif;
  background-color: white;
}

.search-label {
  margin-bottom: 10px;
  font-size: 16px;
  color: #555;
}

.grid-table {
  border: 1px solid #ccc;
  padding: 20px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: scroll;
  max-height: 700px;
}

.member-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 6px;
  overflow: hidden;
}

th {
  background-color: #dce0e5;
  color: #333;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid #ddd;
}

td {
  padding: 12px;
  border-bottom: 1px solid #eee;
  font-size: 17px;
  color: #333;
}

tr:last-child td {
  border-bottom: none;
}

.member-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.member-icon {
  margin-top: 5px;
  font-size: 20px;
  color: #666;
}

.member-info img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.member-name {
  font-weight: 600;
  color: #333;
}

.member-company {
  font-weight: 500;
  font-size: 15px;
  color: #777;
}

.status-pill {
  padding: 6px 10px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  display: inline-block;
  min-width: 90px;
}

.status-pill.active {
  background-color: #9effb3;
  color: #00a81c;
}

.status-pill.lapsed {
  background-color: #ffed9e;
  color: #db6e00;
}

.status-pill.no-payments {
  background-color: #ffc8c8;
  color: #c6432e;
}

.view-btn {
  background-color: #f29900;
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s ease;
  min-width: 100px;
  font-weight: 500;
}

.view-btn:hover {
  background-color: #d97706;
}

.grid-table tbody tr:nth-child(odd) {
  background-color: #f9fafc;
}

.grid-table tbody tr:nth-child(even) {
  background-color: white;
}

.uppercase-text {
  text-transform: uppercase;
}

@media screen and (max-width: 768px) {
  html,
  body {
    height: auto;
    overflow: auto;
    scroll-behavior: smooth;
  }

  .main-container {
    height: auto;
    overflow-y: auto;
  }

  .grid-table {
    padding: 0;
    border: none;
    box-shadow: none;
    overflow: visible !important;
    max-height: none !important;
  }

  .member-table {
    border: none;
    border-collapse: separate;
    border-spacing: 0 10px;
    font-size: 14px;
  }

  .member-table thead {
    display: none;
  }

  .member-table tr {
    display: block;
    margin-bottom: 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    background-color: white;
    padding: 15px;
  }

  .grid-table tbody tr:nth-child(odd),
  .grid-table tbody tr:nth-child(even) {
    background-color: #f6f7f9;
  }

  .member-table td {
    display: block;
    text-align: right;
    padding: 6px 0;
    border-bottom: none;
    position: relative;
    font-size: 14px;
  }

  .member-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .member-info::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    border-bottom: 1px solid #ddd;
  }
  .member-table td:nth-child(1) {
    text-align: left;
    padding-top: 8px;
    padding-bottom: 12px;
    font-size: 14px;
    font-weight: 600;
  }

  .member-table td[data-label]::before {
    content: attr(data-label) ": ";
    font-weight: bold;
    color: #555;
    float: left;
    margin-right: 10px;
    font-size: 14px;
  }

  .member-company {
    font-size: 12px;
    color: #888;
    font-weight: 500;
    margin-top: 4px;
  }

  .member-table td:last-child {
    text-align: center;
    padding-top: 15px;
  }

  .member-table td:last-child .view-btn {
    width: 100%;
    padding: 12px;
  }
}
</style>
